import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { AutoComplete } from 'primereact/autocomplete'
import { DropdownChangeParams } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { classNames } from 'primereact/utils'
import { useTranslation } from 'react-i18next'
import { MultiSelectPanelHeaderTemplateParams } from 'primereact/multiselect'

// local imports
import location from 'assets/icons/location.svg'
import TracingService from 'service/TracingService'
import AuthUtil from 'utils/AuthUtil'
import { IEmployeeSummary } from 'types/Employee'
import { ISecurityLocations, CampusServiceTypes } from 'types/TrackingType'
import DatePickerIcon from 'components/ui/DatePickerIcon'
import { Line } from './styled'
import { MultiSelectWrapper, StyledMultiSelect } from 'components/map/styled'
import styled from 'styled-components'

interface ITracingSearchProps {
  selectedLocation: any
  setSelectedLocation: Dispatch<SetStateAction<any>>
  endDate: Date
  setEndDate: Dispatch<SetStateAction<Date | Date[] | undefined>>
  startDate: Date
  setStartDate: Dispatch<SetStateAction<Date | Date[] | undefined>>
  selectedService: CampusServiceTypes[]
  setSelectedService: Dispatch<SetStateAction<CampusServiceTypes[]>>
  getTableList: () => void
}

export default function TracingSearchForm(props: ITracingSearchProps) {
  const {
    selectedLocation,
    setSelectedLocation,
    endDate,
    setEndDate,
    startDate,
    setStartDate,
    selectedService,
    setSelectedService,
    getTableList,
  } = props

  const { t } = useTranslation()

  /* Form Input States */
  const [filteredLocation, setFilteredLocation] = useState<any>(null)
  const [countries, setCountries] = useState<any>([])
  const [minDate, setMinDate] = useState<Date | undefined>()
  const [maxDate, setMaxDate] = useState<Date | undefined>()
  const employee = AuthUtil.getEmployeeSummary() as IEmployeeSummary

  const campusServices = [
    { value: 1, label: t('campus-services.health-service') },
    { value: 2, label: t('campus-services.coiffeur') },
    { value: 3, label: t('campus-services.gym') },
    { value: 4, label: t('campus-services.lactation-room') },
  ]

  const onClickLocation = () => {
    var button = document.getElementsByClassName('p-button p-component p-autocomplete-dropdown p-button-icon-only')
    if (button !== undefined && button.length > 0) {
      // @ts-ignore
      button[0].click()
    }
  }

  const initSearchButtonDisable = () => {
    var dropdownButton = document.getElementsByClassName(
      'p-button p-component p-autocomplete-dropdown p-button-icon-only'
    )
    if (dropdownButton !== undefined && dropdownButton.length > 0) {
      dropdownButton[0].classList.add('d-none')
    }
  }

  const initSetDate = () => {
    let nowMin = new Date()
    let nowMax = new Date()
    nowMin.setMonth(nowMin.getMonth() - 6)
    nowMax.setMonth(nowMax.getMonth() + 6)
    setMinDate(nowMin)
    setMaxDate(nowMax)
  }

  useEffect(() => {
    initSetDate()
    initSearchButtonDisable()

    /* Get Location List */
    TracingService.getGrantedSecurityLocations(true).then((response) => {
      let res = response.data
      res.map(
        (location) =>
          (location._fullLocationName =
            location.locationName +
            ' (' +
            location.firmName +
            ') • ' +
            location.districtName +
            '/' +
            location.cityName +
            '/' +
            location.countryName)
      )
      setCountries(res)
      const userLocation = res.find((fl: ISecurityLocations) => fl.locationId === employee.locationId)
      setSelectedLocation(userLocation)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchLocation = (event: { query: string }) => {
    setTimeout(() => {
      let _filteredLocation
      if (!event.query.trim().length) {
        _filteredLocation = [...countries]
      } else {
        _filteredLocation = countries.filter((country: { firmName: string }) => {
          return country.firmName.toLowerCase().startsWith(event.query.toLowerCase())
        })
      }
      setFilteredLocation(_filteredLocation)
    }, 250)
  }

  const handleSelectLocation = (e: DropdownChangeParams) => {
    setSelectedLocation(e.value)
  }

  const handleClearLocation = () => {
    setSelectedLocation(null)
  }

  const itemTemplate = (item: any) => {
    return (
      <div className="d-flex align-items-center">
        <img src={location} className="ms-2 me-3 of-location-img" alt="location" />
        <div className="firm-info py-2">
          <div className={'firm-name'}>{item.firmName}</div>
          <div className={'firm-district-name'}>{`${item.districtName} | ${item.cityName} | ${item.countryName}`}</div>
          <div className={'firm-location-name'}>{item.locationName}</div>
        </div>
      </div>
    )
  }

  const hasLocationSelected = typeof selectedLocation === 'object' && selectedLocation !== null

  /* Templates (choose all) */
  function panelHeaderTemplate(options: MultiSelectPanelHeaderTemplateParams) {
    return (
      <div className="p-multiselect-header">
        <label className="d-flex align-items-center">
          {options.checkboxElement}
          <span role="button" className="px-2 user-select-none">
            {t('buttons.choose-all')}
          </span>
        </label>
        {options.closeElement}
      </div>
    )
  }

  return (
    <div className="tracing-search">
      <StyledDiv className="campusService">
        <MultiSelectWrapper>
          <label className="autocomplete-title">{t('input-texts.choose-service')}</label>
          <StyledMultiSelect
            id="campusServiceType"
            name="resType"
            selectedItemsLabel={t('most-used-words.all-selected')}
            filter={false}
            scrollHeight="145px"
            maxSelectedLabels={3}
            optionLabel="label"
            optionValue="value"
            panelHeaderTemplate={panelHeaderTemplate}
            options={campusServices}
            value={selectedService}
            onChange={(e) => setSelectedService(e.value)}
            placeholder={t('input-texts.choose-service-placeholder')}
          />
        </MultiSelectWrapper>
      </StyledDiv>

      <div className={classNames('location-autocomplete', hasLocationSelected ? 'chips' : '')}>
        <span className="p-input-icon-left w-100">
          <i className="pi pi-map-marker" style={{ zIndex: 3 }} />
          <AutoComplete
            value={selectedLocation}
            field="_fullLocationName"
            onClick={
              typeof selectedLocation !== 'object' || selectedLocation === null ? () => onClickLocation() : () => {}
            }
            suggestions={filteredLocation}
            className={classNames('w-100', hasLocationSelected ? 'location-input' : '')}
            dropdown
            completeMethod={searchLocation}
            forceSelection
            itemTemplate={itemTemplate}
            readOnly={hasLocationSelected}
            onChange={handleSelectLocation}
            placeholder={t('input-texts.location-choice')}
          />
          {hasLocationSelected && (
            <Button
              icon="pi pi-times"
              className="p-button-rounded p-button-text p-button-plain"
              style={{ position: 'absolute', right: '4px', top: '6px' }}
              onClick={handleClearLocation}
            />
          )}
        </span>
      </div>

      <Line className="pi pi-ellipsis-h" />

      <StyledDiv className="campusDate">
        <DatePickerIcon
          showOtherMonths
          selectOtherMonths
          disabledDays={[]}
          idName={'campus-tracing-start-date'}
          defaultDate={startDate}
          minDate={minDate}
          maxDate={maxDate}
          onSelectedDateChange={(e) => setStartDate(e.value)}
          label={t('input-texts.start-date')}
        />
      </StyledDiv>

      <StyledDiv className="campusDate">
        <DatePickerIcon
          showOtherMonths
          selectOtherMonths
          disabledDays={[]}
          idName={'campus-tracing-end-date'}
          defaultDate={endDate}
          minDate={minDate}
          maxDate={maxDate}
          onSelectedDateChange={(e) => setEndDate(e.value)}
          label={t('input-texts.end-date')}
        />
      </StyledDiv>

      <Button
        style={{ minWidth: '100px', maxWidth: '180px' }}
        className="search-button"
        label={t('buttons.update-list')}
        onClick={getTableList}
      />
    </div>
  )
}

const StyledDiv = styled.div`
  &.campusService {
    width: 280px;
    max-width: 300px;
    @media (max-width: 1200px) {
      min-width: 185px;
      max-width: 200px;
    }
  }

  &.campusDate {
    min-width: 145px;
    max-width: 185px;

    @media (max-width: 1200px) {
      width: 110px;
      max-width: 135px;
    }
  }
`
